import React, { useState, useEffect } from "react";
import { Container, Row, Col, Alert } from "reactstrap";
import StatsWidget from "./components/StatsWidget";
import AlertsBySeverity from "./components/AlertsBySeverity";
import TopThreatTypes from "./components/TopThreatTypes";
import MitreAttackTechniques from "./components/MitreAttackTechniques";
import DetectedIOCs from "./components/DetectedIOCs";
import AlertsOverTime from "./components/AlertsOverTime";
import AgentSecurityStatus from "./components/AgentSecurityStatus";
import HourlyAlertDistribution from "./components/HourlyAlertDistribution";
import ComplianceViolations from "./components/ComplianceViolations";
import CriticalThreatsTable from "./components/CriticalThreatsTable";
import WindowsThreatsTable from "./components/WindowsThreatsTable";
import Office365ThreatsTable from "./components/Office365ThreatsTable";
import { fetchAPI } from "./utils/api";
import { hasFeature } from "../../helpers/features";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const pageHeadingStyle = {
  color: "var(--vz-body-color)",
};

const pageIconWrapStyle = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid rgba(var(--vz-primary-rgb), 0.35)",
  background: "rgba(var(--vz-primary-rgb), 0.08)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const pageIconStyle = {
  color: "var(--vz-primary)",
  fontSize: 16,
};

const ThreatFeed = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const showOffice365 = hasFeature('office365');

  useEffect(() => {
    document.title = "Security Dashboard | Threat Intelligence";
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await fetchAPI('/threats/dashboard/overview');
      
      if (response.success) {
        setDashboardData(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const stats = dashboardData?.stats || {};

  return (
    <div className="page-content">
      <Container fluid>
        {/* Page Title */}
        <Row className="mb-4">
          <Col>
            <h4 className="mb-0" style={pageHeadingStyle}>
              <div style={pageIconWrapStyle} className="me-2">
                <i className="ri-dashboard-line" style={pageIconStyle}></i>
              </div>
              Security Dashboard
            </h4>
            <p className="text-muted mb-0 mt-2" style={{ fontSize: "13px" }}>
              Real-time threat intelligence and security monitoring
            </p>
          </Col>
        </Row>

        {error && (
          <Row className="mb-3">
            <Col xs="12">
              <Alert 
                color="danger"
                style={{
                  backgroundColor: 'rgba(220, 53, 69, 0.1)',
                  border: '1px solid rgba(220, 53, 69, 0.3)',
                  color: '#dc3545'
                }}
              >
                <i className="ri-error-warning-line me-2"></i>
                Error loading dashboard: {error}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Stats Widgets Row */}
        <Row className="g-3 mb-4">
          <StatsWidget 
            title="Total Threats" 
            value={stats.total_threats || 0}
            subtext={`${stats.new_24h || 0} new in last 24h`}
            color={ORANGE_COLORS.primary}
            icon="ri-shield-flash-line"
            loading={loading}
          />
          <StatsWidget 
            title="Critical Alerts" 
            value={stats.critical || 0}
            subtext={`${stats.critical || 0} critical threats`}
            color="#8b0000"
            icon="ri-alarm-warning-line"
            loading={loading}
          />
          <StatsWidget 
            title="High Alerts" 
            value={stats.high || 0}
            subtext={`${stats.high || 0} high severity`}
            color="#dc3545"
            icon="ri-error-warning-line"
            loading={loading}
          />
          <StatsWidget 
            title="Affected Assets" 
            value={stats.unique_assets || 0}
            subtext="Unique systems"
            color={ORANGE_COLORS.dark}
            icon="ri-server-line"
            loading={loading}
          />
          <StatsWidget 
            title="Attack Sources" 
            value={stats.unique_ips || 0}
            subtext="Unique IPs"
            color={ORANGE_COLORS.darker}
            icon="ri-global-line"
            loading={loading}
          />
        </Row>

        {/* Charts Row 1 */}
        <Row className="mb-4">
          <Col xl={6}>
            <AlertsBySeverity data={dashboardData?.severity} loading={loading} />
          </Col>
          <Col xl={6}>
            <TopThreatTypes data={dashboardData?.threat_types} loading={loading} />
          </Col>
        </Row>

        {/* Charts Row 2 */}
        <Row className="mb-4">
          <Col xl={6}>
            <MitreAttackTechniques data={dashboardData?.mitre_techniques} loading={loading} />
          </Col>
          <Col xl={3}>
            <DetectedIOCs data={dashboardData?.ioc_distribution} loading={loading} />
          </Col>
          <Col xl={3}>
            <AlertsOverTime data={dashboardData?.recent_activity} loading={loading} />
          </Col>
        </Row>

        {/* Charts Row 3 */}
        <Row className="mb-4">
          <Col xl={3}>
            <AgentSecurityStatus loading={loading} />
          </Col>
          <Col xl={6}>
            <HourlyAlertDistribution data={dashboardData?.recent_activity} loading={loading} />
          </Col>
          <Col xl={3}>
            <ComplianceViolations loading={loading} />
          </Col>
        </Row>

        {/* Critical Threats Table */}
        <Row className="mb-4">
          <Col>
            <CriticalThreatsTable data={dashboardData?.critical_threats} loading={loading} />
          </Col>
        </Row>

        {/* Windows Threats Table */}
        <Row className="mb-4">
          <Col>
            <WindowsThreatsTable loading={loading} />
          </Col>
        </Row>

        {/* Office 365 Threats Table — only for tenants with the 'office365' feature */}
        {showOffice365 && (
          <Row className="mb-4">
            <Col>
              <Office365ThreatsTable loading={loading} />
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default ThreatFeed;